import { POSTCODE_KEY } from '@core/Constants/Filters/LocationFilter.constants';
import { isWindowDefined } from '@core/Constants/Window';
import { mappedLocales } from '@core/Utils/Intl/MappedLocales';

import { AutoComplete } from '@gds/Autocomplete/AutoComplete';
import Button from '@gds/Button/Button';
import { Location as LocationIcon } from '@gds/Icons/Paths/Location';
import { SegmentedButtons } from '@gds/SegmentedButtons/SegmentedButtons';
import Slider from '@gds/Slider/Slider';
import { Typography } from '@gds/Typography/Typography';

import { useSetAtom } from 'jotai';

import { openQuickFilterAtom } from 'Atoms/App/Plp/QuickFilterOpenAtom';
import { PlpFiltersMessagesProps } from 'Components/Plp/Filters/FilterPanel.entity';

import ApplyButton from '../ApplyButton/ApplyButton';

import { OPTIONS } from './LocationFilter.constants';

import { LocationFilterProps, LocationType } from './LocationFilter.entity';

import useLocationFilter from './UseLocationFilter';

import styles from './LocationFilter.module.css';

export const LocationFilter = ({ strings, locale, applyButton }: LocationFilterProps) => {
  const {
    handleChange,
    handleInputChange,
    canReset,
    resetAllFilter,
    suggestions,
    searchValue,
    setSearchValue,
    locationType,
    setLocationType,
    sliderProps,
    error,
    totalCount,
    handleApply,
    localSliderDisplayValue,
  } = useLocationFilter(strings.errorMessage, locale, applyButton);

  const setOpenQuickFilter = useSetAtom(openQuickFilterAtom);

  const btnDataIntl = OPTIONS.map(type => ({
    ...type,
    label: strings[type.id as keyof PlpFiltersMessagesProps],
  }));

  const pathname = isWindowDefined && window.location.pathname;

  const shouldHideRangeFilters = pathname
    ? pathname.includes('locations') || pathname.includes('dealerships')
    : false;

  const handleLocationApply = () => {
    handleApply();
    setOpenQuickFilter(null);
  };

  return (
    <section className={styles.locationFilter}>
      <SegmentedButtons
        buttons={btnDataIntl}
        className={styles.locationTypeBtns}
        currentValue={locationType}
        onChange={val => {
          setLocationType(val as LocationType);
        }}
      />

      <div className={styles.inputs}>
        <AutoComplete
          onReset={() => {
            setSearchValue('');
          }}
          rightIcon={<LocationIcon />}
          autoComplete={locationType !== POSTCODE_KEY}
          options={suggestions}
          error={error}
          onChange={e => {
            e && handleChange(e);
          }}
          value={searchValue}
          placeholder={strings[locationType]}
          inputChange={handleInputChange}
        />
      </div>
      {locationType === POSTCODE_KEY && !shouldHideRangeFilters && (
        <Slider
          {...sliderProps}
          selectedRangeIndexes={
            sliderProps.selectedRangeIndexes.length === 2
              ? [sliderProps.selectedRangeIndexes[0], sliderProps.selectedRangeIndexes[1]]
              : sliderProps.selectedRangeIndexes.length === 1
              ? [sliderProps.selectedRangeIndexes[0]]
              : undefined
          }
        />
      )}
      {!shouldHideRangeFilters && (
        <div className={styles.auxInfo}>
          <div>
            {locationType === POSTCODE_KEY && (
              <Typography Component="span" variant="subheading3">
                {!applyButton
                  ? strings.distance
                  : localSliderDisplayValue === 500
                  ? strings.radiusTerritory
                  : `${strings.distanceLabel}: ${localSliderDisplayValue} km`}
              </Typography>
            )}
            <Typography Component="span" className={styles.resultCount} variant="caption2">
              ({totalCount.toLocaleString(mappedLocales[locale])})
            </Typography>
          </div>
          {!applyButton && (
            <Button
              color="secondary"
              disabled={!canReset()}
              onClick={resetAllFilter}
              size="small"
              variant="link"
            >
              {strings.reset}
            </Button>
          )}
        </div>
      )}
      {applyButton && <ApplyButton handleApply={handleLocationApply} ctaText={strings.apply} />}
    </section>
  );
};
